import { Link, useParams } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import CharacterCard from "@/components/base/CharacterCard";
import { usePageMeta } from "@/hooks/usePageMeta";
import { characters, getCharacter } from "@/mocks/characters";
import { getWork } from "@/mocks/works";

export default function CharacterDetail() {
  const { slug } = useParams<{ slug: string }>();
  const character = slug ? getCharacter(slug) : undefined;

  usePageMeta(
    character
      ? `${character.name}｜${character.title} - 韩国妖精漫画角色图鉴`
      : "角色未找到 - 韩国妖精漫画角色图鉴",
    character
      ? `${character.name}是韩国妖精漫画《${character.workName}》的女主角，萌娘属性：${character.tags.join("、")}，主要CP：${character.cp}。查看${character.name}的人设、性格与CP组合介绍。`
      : "没有找到对应的角色，请回到角色图鉴重新查找。",
    character
      ? {
          "@context": "https://schema.org",
          "@type": "Person",
          name: character.name,
          jobTitle: character.title,
          description: `${character.name}，韩国妖精漫画《${character.workName}》女主角，${character.race}，萌娘属性：${character.tags.join("、")}。`,
          knowsAbout: character.cp,
        }
      : undefined
  );

  if (!character) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="mx-auto flex max-w-7xl flex-col items-center gap-4 px-4 py-24 text-center">
          <span className="flex h-16 w-16 items-center justify-center rounded-full bg-background-100 text-foreground-400">
            <i className="ri-emotion-sad-line text-3xl" aria-hidden="true"></i>
          </span>
          <h1 className="font-heading text-xl font-bold text-foreground-950">没有找到这位女主</h1>
          <p className="text-sm text-foreground-600">可能角色资料还在整理中，回图鉴看看其他漂亮女主吧。</p>
          <Link
            to="/encyclopedia"
            className="mt-2 inline-flex items-center gap-2 whitespace-nowrap rounded-full bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
          >
            返回角色图鉴
          </Link>
        </main>
        <Footer />
      </div>
    );
  }

  const work = getWork(character.workSlug);
  const related = characters.filter((c) => c.workSlug === character.workSlug && c.slug !== character.slug);
  const others = characters.filter((c) => c.workSlug !== character.workSlug).slice(0, 4);

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />
      <main>
        <section className="bg-background-100/60">
          <div className="mx-auto max-w-7xl px-4 md:px-6 py-8 md:py-12">
            <nav aria-label="面包屑" className="flex flex-wrap items-center gap-2 text-xs text-foreground-500">
              <Link to="/" className="hover:text-primary-600">首页</Link>
              <i className="ri-arrow-right-s-line" aria-hidden="true"></i>
              <Link to="/encyclopedia" className="hover:text-primary-600">角色图鉴</Link>
              <i className="ri-arrow-right-s-line" aria-hidden="true"></i>
              <span className="text-foreground-700">{character.name}</span>
            </nav>

            <div className="mt-6 grid grid-cols-1 gap-8 lg:grid-cols-[360px_1fr]">
              <div className="relative overflow-hidden rounded-3xl border border-background-200 bg-background-50">
                <div className="aspect-[3/4] overflow-hidden bg-background-100">
                  <img
                    src={character.avatar}
                    alt={`${character.name} · ${character.title}`}
                    title={`${character.name} ${character.title}`}
                    className="h-full w-full object-cover object-top"
                  />
                </div>
                <div className="flex items-center justify-between p-4">
                  <span className="rounded-full bg-secondary-100 px-3 py-1 text-xs font-medium text-secondary-900">
                    {character.race}
                  </span>
                  <span className="flex items-center gap-1 rounded-full bg-accent-100 px-3 py-1 text-xs font-semibold text-accent-800">
                    <i className="ri-fire-fill" aria-hidden="true"></i>
                    人气 {character.popularity}
                  </span>
                </div>
              </div>

              <div>
                <span className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-primary-600">
                  <span className="h-px w-6 bg-primary-400" aria-hidden="true"></span>
                  {character.workName} · {character.role}
                </span>
                <h1 className="mt-3 font-heading text-3xl font-bold text-foreground-950 md:text-4xl">{character.name}</h1>
                <p className="mt-2 text-lg text-primary-600">{character.title}</p>

                <div className="mt-4 flex flex-wrap gap-2">
                  {character.tags.map((t) => (
                    <Link
                      key={t}
                      to={`/encyclopedia?tag=${encodeURIComponent(t)}`}
                      className="rounded-full bg-secondary-100 px-3 py-1 text-sm text-secondary-900 hover:bg-secondary-200 transition-colors"
                    >
                      #{t}
                    </Link>
                  ))}
                </div>

                <div className="mt-7 grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <div className="rounded-2xl border border-background-200 bg-background-50 p-5">
                    <div className="flex items-center gap-2">
                      <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-primary-100 text-primary-600">
                        <i className="ri-user-heart-line text-lg" aria-hidden="true"></i>
                      </span>
                      <h2 className="font-heading text-base font-bold text-foreground-950">角色人设</h2>
                    </div>
                    <p className="mt-3 text-sm leading-relaxed text-foreground-700">{character.intro}</p>
                  </div>
                  <div className="rounded-2xl border border-background-200 bg-background-50 p-5">
                    <div className="flex items-center gap-2">
                      <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-accent-100 text-accent-700">
                        <i className="ri-heart-2-line text-lg" aria-hidden="true"></i>
                      </span>
                      <h2 className="font-heading text-base font-bold text-foreground-950">性格魅力</h2>
                    </div>
                    <p className="mt-3 text-sm leading-relaxed text-foreground-700">{character.personality}</p>
                  </div>
                </div>

                <div className="mt-4 rounded-2xl bg-foreground-950 p-5 text-background-100">
                  <div className="flex flex-wrap items-center justify-between gap-3">
                    <div className="flex items-center gap-2">
                      <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-primary-500 text-background-50">
                        <i className="ri-hand-heart-line text-lg" aria-hidden="true"></i>
                      </span>
                      <div>
                        <p className="text-xs text-background-300">主要 CP 组合</p>
                        <p className="text-sm font-semibold text-background-50">{character.cp}</p>
                      </div>
                    </div>
                  </div>
                  <p className="mt-3 text-sm leading-relaxed text-background-200">{character.cpDesc}</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="mx-auto max-w-7xl px-4 md:px-6 py-10 md:py-14">
          <div className="grid grid-cols-1 gap-10 lg:grid-cols-3">
            <div className="lg:col-span-1">
              <h2 className="font-heading text-lg font-bold text-foreground-950">所属作品</h2>
              {work && (
                <div className="mt-4 rounded-2xl border border-background-200 bg-background-50 p-4">
                  <div className="flex gap-4">
                    <img
                      src={work.cover}
                      alt={`《${work.title}》封面`}
                      className="h-28 w-20 rounded-xl object-cover"
                    />
                    <div className="flex flex-1 flex-col">
                      <h3 className="font-heading text-base font-bold text-foreground-950">《{work.title}》</h3>
                      <p className="mt-1 text-xs text-foreground-500">{work.status} · {work.chapters}</p>
                      <p className="mt-1 text-xs text-foreground-500">{work.genre.join(" / ")} · 评分 {work.rating.toFixed(1)}</p>
                      <Link to="/works" className="mt-auto inline-flex items-center gap-1 text-sm font-medium text-primary-600 hover:text-primary-700">
                        查看作品
                        <i className="ri-arrow-right-line text-xs" aria-hidden="true"></i>
                      </Link>
                    </div>
                  </div>
                </div>
              )}
              <h3 className="mt-8 font-heading text-base font-bold text-foreground-950">本作其他女主</h3>
              <div className="mt-4 space-y-3">
                {related.length ? (
                  related.map((c) => (
                    <Link
                      key={c.slug}
                      to={`/character/${c.slug}`}
                      className="flex items-center gap-3 rounded-2xl border border-background-200 bg-background-50 p-3 transition-colors hover:border-primary-300"
                    >
                      <img src={c.avatar} alt={`${c.name} 头像`} className="h-12 w-12 rounded-xl object-cover" />
                      <div>
                        <p className="text-sm font-semibold text-foreground-950">{c.name}</p>
                        <p className="text-xs text-foreground-500">{c.title}</p>
                      </div>
                    </Link>
                  ))
                ) : (
                  <p className="text-sm text-foreground-500">暂无其他已收录女主。</p>
                )}
              </div>
            </div>

            <div className="lg:col-span-2">
              <h2 className="font-heading text-lg font-bold text-foreground-950">你可能也想了解</h2>
              <div className="mt-4 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-2">
                {others.map((c) => (
                  <CharacterCard key={c.slug} character={c} />
                ))}
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}